/** Shared product-category tree node shape for web nav/filter/manage UIs. */

import type { TreeNode } from '../../core/models/component.model';
import type { MaterialProductCategoryDto } from '@core/shared-bridge';
import type { MaterialCatalogNavNode } from './material-catalog-admin-table.util';
import {
  isObecneCategory,
  productCategoryChildrenMap,
  resolveObecneRootId,
} from './product-category-tree.util';

export interface ProductCategoryTreeNode {
  id: string;
  label: string;
  children: ProductCategoryTreeNode[];
  /** System root category (no parent). */
  isSystemRoot?: boolean;
  /** System-managed category (limited delete). */
  isSystem?: boolean;
  /** Caller-specific payload (e.g. `TreeNode` for SAP filtering). */
  source?: unknown;
}

export function toggleProductCategoryExpanded(
  expanded: ReadonlySet<string>,
  id: string,
): Set<string> {
  const next = new Set(expanded);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export function findProductCategoryTreeNode(
  nodes: readonly ProductCategoryTreeNode[],
  id: string,
): ProductCategoryTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findProductCategoryTreeNode(node.children, id);
    if (found) return found;
  }
  return null;
}

/** Number of descendant category nodes (not including `node` itself). */
export function countProductCategoryDescendants(node: ProductCategoryTreeNode): number {
  return (node.children ?? []).reduce(
    (sum, child) => sum + 1 + countProductCategoryDescendants(child),
    0,
  );
}

function componentTreeNodeId(node: TreeNode): string {
  const value = String(node.value ?? '').trim();
  if (value) return value;
  return `label:${node.type}:${node.label}`;
}

/** `GET /components/tree` nodes (Sklady). */
export function componentTreeNodesToProductCategory(
  nodes: readonly TreeNode[],
): ProductCategoryTreeNode[] {
  return nodes.map((node) => ({
    id: componentTreeNodeId(node),
    label: node.label,
    children: componentTreeNodesToProductCategory(node.children ?? []),
    source: node,
  }));
}

/** Produkty filter nav tree (`MaterialCatalogNavNode`). */
export function navTreeNodesToProductCategory(
  nodes: readonly MaterialCatalogNavNode[],
): ProductCategoryTreeNode[] {
  return nodes.map((node) => ({
    id: node.categoryId ?? `nav:${node.label}`,
    label: node.label,
    children: navTreeNodesToProductCategory(node.children),
    source: node,
  }));
}

/** Produkty → Kategorie manage (`MaterialProductCategoryDto` flat list). */
export function categoriesToProductCategoryTree(
  categories: readonly MaterialProductCategoryDto[],
  roots?: readonly MaterialProductCategoryDto[],
): ProductCategoryTreeNode[] {
  const byParent = productCategoryChildrenMap([...categories]);
  const rootList = roots ?? categories.filter((c) => !c.parentId);
  const walk = (cat: MaterialProductCategoryDto): ProductCategoryTreeNode => ({
    id: cat.id,
    label: cat.name,
    isSystemRoot: cat.isSystem && !cat.parentId,
    isSystem: cat.isSystem,
    children: (byParent.get(cat.id) ?? []).map(walk),
    source: cat,
  });
  return [...rootList]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'cs'))
    .map(walk);
}

/**
 * Správa kategorií: potomci Obecné jako kořeny, ostatní systémové kořeny zůstávají.
 */
export function categoriesToProductCategoryManageTree(
  categories: readonly MaterialProductCategoryDto[],
): ProductCategoryTreeNode[] {
  const all = [...categories];
  const obecneRootId = resolveObecneRootId(all);
  const objectTypeRoots = all.filter(
    (cat) =>
      !cat.parentId &&
      !isObecneCategory(cat, obecneRootId ?? undefined) &&
      cat.objectTypeKey !== 'OBECNE',
  );
  const obecneChildRoots =
    obecneRootId != null ? all.filter((cat) => cat.parentId === obecneRootId) : [];
  const manageRoots = [...obecneChildRoots, ...objectTypeRoots].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'cs'),
  );
  return categoriesToProductCategoryTree(all, manageRoots);
}

export function productCategoryTreeRowPadding(depth: number): number {
  return 0.5 + depth * 0.75;
}

export function productCategoryTreeManageRowPadding(depth: number): number {
  return 0.5 + depth * 0.85;
}

/** File-tree click: expand/collapse folders; optionally also select (in-place filter). */
export type ProductCategoryTreeClickAction = 'toggle' | 'toggle-select' | 'select' | 'none';

export function productCategoryTreeClickAction(args: {
  hasChildren: boolean;
  clickDetail: number;
  selectionEnabled: boolean;
  /** Zásoby: folder click expands and filters the stock table. Katalog: expand only. */
  selectFolderOnClick?: boolean;
}): ProductCategoryTreeClickAction {
  if (args.clickDetail > 1) return 'none';
  if (args.hasChildren) {
    if (args.selectFolderOnClick && args.selectionEnabled) return 'toggle-select';
    return 'toggle';
  }
  return args.selectionEnabled ? 'select' : 'none';
}
